/**
 * <as-auto-controls>
 *
 * Turn-key parameter UI: renders a full two-step <as-step-selector> and auto-builds
 * the Edit step's controls from the configurator's params — the modern replacement
 * for Paramate's vendored `ControlsAuto` (paramateControlsAuto.js).
 *
 *   Edit    — one self-syncing as-components input per used/visible param, bucketed
 *             into <as-param-group> sections by each param's `style` group/group-title
 *             (the same grouping the old `grouping()` post-processor did, done here).
 *   Export  — <as-export-preview-panel> + <as-export-step>.
 *
 * Params are read from the <as-configurator> element in the page (via getParams(),
 * which waits for init). The generated inputs bind via `trcad-param` and sync their
 * own value/min/max to Paramate — no per-configure rebuild needed.
 *
 * Built in light DOM (like <as-export-step>) so the step selector and inputs
 * participate in normal layout and register with the store. The host uses
 * `display: contents` so the inner <as-step-selector> sits directly in the parent.
 *
 * Attributes
 *   edit-label    — Edit step label   (default "Edit")
 *   export-label  — Export step label (default "Export")
 *
 * Accessors
 *   .exportStep   — the inner <as-export-step> (configure scopes/packageName)
 */

import '../text-and-lang/as-text.ts';
import '../parameters/as-param-group.ts';
import '../step-selector/as-step-panel.ts';
import '../step-selector/step-selector.ts';
import '../inputs/numbox.ts';
import '../inputs/as-checkbox.ts';
import '../inputs/as-dropdown.ts';
import '../inputs/as-option.ts';
import '../inputs/as-string-input.ts';
import '../file/as-file-input.ts';
import '../file/as-export-step.ts';
import '../file/as-export-preview-panel.ts';
import { devlog } from '../../logic/utils.ts';

type ParamStyle = Record<string, string>;

interface ParamOption {
  value: string | number;
  text?: string;
}

interface ParamData {
  name?: string;
  descr?: string;
  type?: string;
  value?: unknown;
  used?: boolean;
  style?: string;
  options?: ParamOption[];
}

export class AsAutoControls extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['edit-label', 'export-label'];
  }

  #shellBuilt = false;
  #listening = false;
  #editPanel: HTMLElement | null = null;
  #exportStepEl: HTMLElement | null = null;
  #groups = new Map<string, HTMLElement>();
  #controls = new Map<string, HTMLElement>();
  // Per-control sort key: `order` from the param's style (`order: N`), falling back to
  // source order via a monotonic creation sequence. Drives the order pass.
  #seq = 0;
  #orders = new WeakMap<HTMLElement, { order: number; seq: number }>();

  connectedCallback(): void {
    if (!this.#shellBuilt) this.#buildShell();
    this.#initParams();
    // Re-sync the control set after every configure — params' used/visible state can
    // change, so different controls may need to appear or disappear.
    if (!this.#listening) {
      window.addEventListener('as-paramate-params-updated', this.#onParamsUpdated);
      this.#listening = true;
    }
  }

  disconnectedCallback(): void {
    if (this.#listening) {
      window.removeEventListener('as-paramate-params-updated', this.#onParamsUpdated);
      this.#listening = false;
    }
  }

  #onParamsUpdated = (e: Event): void => {
    const params = (e as CustomEvent).detail?.params as Record<string, ParamData> | undefined;
    if (params) this.#syncControls(params);
  };

  /** The inner <as-export-step>, for configuring scopes / package name. */
  get exportStep(): HTMLElement | null {
    return this.#exportStepEl;
  }

  // ---------------------------------------------------------------------------
  // Shell
  // ---------------------------------------------------------------------------

  #buildShell(): void {
    this.#shellBuilt = true;
    // Transparent to layout: let the inner step selector act as a direct child.
    this.style.display = 'contents';

    const editLabel = this.getAttribute('edit-label') ?? 'Edit';
    const exportLabel = this.getAttribute('export-label') ?? 'Export';

    const frag = AsAutoControls.#html(/*html*/ `
      <as-step-selector focus-on-change>
        <as-step-panel id="auto-edit-panel" label-default-text="${editLabel}"></as-step-panel>
        <as-step-panel id="auto-export-panel" label-default-text="${exportLabel}">
          <as-export-preview-panel></as-export-preview-panel>
          <as-export-step></as-export-step>
        </as-step-panel>
      </as-step-selector>
    `);

    this.#editPanel = frag.querySelector('#auto-edit-panel');
    this.#exportStepEl = frag.querySelector('as-export-step');
    this.appendChild(frag);
  }

  // ---------------------------------------------------------------------------
  // Params
  // ---------------------------------------------------------------------------

  #initParams(): void {
    const cfgr = document.querySelector('as-configurator') as
      | { getParams?: () => Promise<Record<string, ParamData>> }
      | null;

    if (!cfgr?.getParams) {
      // Configurator element not present/ready yet — retry once it announces ready.
      window.addEventListener('as-cfgr-comp-ready', () => this.#initParams(), { once: true });
      return;
    }

    // getParams() resolves once the configurator is initialized (it polls internally).
    // Subsequent updates come via the 'as-paramate-params-updated' listener.
    cfgr.getParams()
      .then((params) => this.#syncControls(params))
      .catch((err) => devlog('[as-auto-controls] getParams failed:', err));
  }

  /**
   * Reconcile the rendered controls against the current params: add controls for
   * newly used/visible params, remove controls whose param is no longer used/visible,
   * and prune emptied groups. Existing controls are left in place so they keep their
   * focus and self-synced value/min/max — only the changed set is touched.
   */
  #syncControls(params: Record<string, ParamData>): void {
    if (!this.#editPanel || !params) return;

    const desired = new Set<string>();

    for (const key in params) {
      if (!Object.prototype.hasOwnProperty.call(params, key)) continue;
      const param = params[key];
      if (!param || !param.used) continue;

      const style = AsAutoControls.#readStyleString(param.style);
      if (style.visible === 'false') continue;

      desired.add(key);

      // Already rendered — keep it (the input self-syncs value/min/max).
      if (this.#controls.has(key)) continue;

      const control = this.#createControl(key, param, style);
      if (!control) {
        desired.delete(key); // unsupported type — nothing to keep
        continue;
      }
      this.#orders.set(control, this.#orderInfo(style));
      this.#containerFor(key, style).appendChild(control);
      this.#controls.set(key, control);
    }

    // Remove controls whose param is no longer used/visible.
    for (const [key, el] of this.#controls) {
      if (!desired.has(key)) {
        el.remove();
        this.#controls.delete(key);
      }
    }

    this.#pruneEmptyGroups();
    this.#applyOrder();
  }

  /** Drop any <as-param-group> that no longer holds controls. */
  #pruneEmptyGroups(): void {
    for (const [groupKey, group] of this.#groups) {
      if (group.childElementCount === 0) {
        group.remove();
        this.#groups.delete(groupKey);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Ordering
  // ---------------------------------------------------------------------------

  /** Sort key for a control: `order: N` from the style, then source/creation order. */
  #orderInfo(style: ParamStyle): { order: number; seq: number } {
    const n = style.order != null && style.order !== '' ? parseFloat(style.order) : NaN;
    return { order: Number.isFinite(n) ? n : Number.POSITIVE_INFINITY, seq: this.#seq++ };
  }

  /**
   * Order pass: sort controls within each group, then sort the groups within each
   * content panel — both keyed by `order` (style) then creation order. Only re-appends
   * when the current DOM order differs, so stable configures don't churn the DOM.
   */
  #applyOrder(): void {
    for (const panel of this.#contentPanels()) {
      for (const group of Array.from(panel.children) as HTMLElement[]) {
        this.#orderChildren(group, (el) => {
          const o = this.#orders.get(el);
          return o ? [o.order, o.seq] : [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY];
        });
      }
      // A group sorts by its lowest-keyed control.
      this.#orderChildren(panel, (group) => this.#groupSortKey(group));
    }
  }

  /** Lowest [order, seq] among a group's controls — the group's own sort key. */
  #groupSortKey(group: HTMLElement): [number, number] {
    let best: [number, number] = [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY];
    for (const child of Array.from(group.children) as HTMLElement[]) {
      const o = this.#orders.get(child);
      if (o && (o.order < best[0] || (o.order === best[0] && o.seq < best[1]))) {
        best = [o.order, o.seq];
      }
    }
    return best;
  }

  /** Re-append a parent's element children in sort-key order, skipping if unchanged. */
  #orderChildren(parent: HTMLElement, keyOf: (el: HTMLElement) => [number, number]): void {
    const items = Array.from(parent.children) as HTMLElement[];
    if (items.length < 2) return;
    const sorted = items.slice().sort((a, b) => {
      const ka = keyOf(a);
      const kb = keyOf(b);
      return ka[0] - kb[0] || ka[1] - kb[1];
    });
    if (items.every((el, i) => el === sorted[i])) return; // already ordered
    for (const el of sorted) parent.appendChild(el);
  }

  /** Content panels controls can be placed into, indexed (panel 0 = Edit). */
  #contentPanels(): HTMLElement[] {
    return this.#editPanel ? [this.#editPanel] : [];
  }

  /**
   * Resolve the <as-param-group> a control belongs in. Every control lives inside a
   * param group — params with an explicit `group` share a titled group; ungrouped
   * params share a single untitled default group per panel. Groups land in the panel
   * named by an explicit `panel:` style, defaulting to panel 0 (Edit). Created on
   * first sight, preserving source order. Group key is namespaced (e.g. `main:chamfer`)
   * to mirror the old grouping() behaviour.
   */
  #containerFor(key: string, style: ParamStyle): HTMLElement {
    const panels = this.#contentPanels();

    // Default to panel 0 (Edit); honour an explicit, in-range `panel:` style.
    let panelIndex = 0;
    if (style.panel != null && style.panel !== '') {
      const parsed = parseInt(style.panel, 10);
      if (Number.isInteger(parsed) && parsed >= 0 && parsed < panels.length) {
        panelIndex = parsed;
      } else {
        devlog(`[as-auto-controls] panel "${style.panel}" for ${key} out of range; using panel 0`);
      }
    }
    const panel = panels[panelIndex];

    const prefix = key.match(/.*:/)?.[0] ?? '';
    const groupKey = style.group ? prefix + style.group : `panel${panelIndex}:__default__`;

    let group = this.#groups.get(groupKey);
    if (!group) {
      group = document.createElement('as-param-group');
      if (style.group) {
        group.setAttribute('subheading-default-text', style['group-title'] || style.group);
      }
      this.#groups.set(groupKey, group);
      panel.appendChild(group);
    }
    return group;
  }

  /** Map a param to the matching as-components input. Returns null for unsupported types. */
  #createControl(key: string, param: ParamData, style: ParamStyle): HTMLElement | null {
    const label = param.name ?? key;
    const display = style.display;
    let el: HTMLElement;

    if ((display === 'dropdown' || display === 'radio') && Array.isArray(param.options)) {
      el = document.createElement('as-dropdown');
      el.setAttribute('label-default-text', label);
      for (const opt of param.options) {
        const option = document.createElement('as-option');
        option.setAttribute('value', String(opt.value));
        option.textContent = opt.text ?? String(opt.value);
        el.appendChild(option);
      }
    } else {
      switch (param.type) {
        case 'bool':
          el = document.createElement('as-checkbox');
          el.setAttribute('label-default-text', label);
          if (param.value != null) el.setAttribute('value', String(param.value));
          break;
        case 'int':
        case 'float':
          el = document.createElement('as-numbox');
          el.setAttribute('label-default-text', label);
          if (param.value != null) el.setAttribute('value', String(param.value));
          if (style.step) el.setAttribute('step', style.step);
          if (style.unit) el.setAttribute('unit', style.unit);
          if (display === 'range') el.setAttribute('show-slider', '');
          break;
        case 'string':
          el = document.createElement('as-string-input');
          el.setAttribute('label-default-text', label);
          if (param.value != null) el.setAttribute('value', String(param.value));
          break;
        case 'file':
          el = document.createElement('as-file-input');
          break;
        default:
          // color / vector / matrix / atrafo / ray / array — not yet supported.
          devlog(`[as-auto-controls] Unsupported param type "${param.type}" for ${key}; skipping`);
          return null;
      }
    }

    // Every control is param-linked and configures on change by default. trCAD can
    // override any attribute via the generic `attr.<name>: <value>` style convention,
    // e.g. `style = "attr.configure-on-change: false;"` to opt a param out.
    el.setAttribute('trcad-param', key);
    if (param.descr) el.setAttribute('help-tooltip-default-text', param.descr);
    el.setAttribute('configure-on-change', '');
    this.#applyStyleAttrs(el, style);

    return el;
  }

  /** Apply generic `attr.<name>: <value>` style directives onto a control. */
  #applyStyleAttrs(el: HTMLElement, style: ParamStyle): void {
    for (const key in style) {
      if (!key.startsWith('attr.')) continue;
      const name = key.slice(5).trim();
      if (name) AsAutoControls.#applyAttr(el, name, style[key]);
    }
  }

  /**
   * Set an attribute from a style value. A value of "false" removes the attribute
   * (so boolean attrs like configure-on-change can be turned off); "" or "true"
   * sets it present; anything else sets it verbatim.
   */
  static #applyAttr(el: HTMLElement, name: string, raw: string): void {
    const v = (raw ?? '').trim();
    if (v.toLowerCase() === 'false') {
      el.removeAttribute(name);
    } else if (v === '' || v.toLowerCase() === 'true') {
      el.setAttribute(name, '');
    } else {
      el.setAttribute(name, v);
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /** Parse a Paramate `style` string ("key: value; key: value") into an object. */
  static #readStyleString(style?: string): ParamStyle {
    const out: ParamStyle = {};
    if (!style) return out;
    for (const part of style.split(';')) {
      const idx = part.indexOf(':');
      if (idx === -1) continue;
      const k = part.slice(0, idx).trim();
      if (!k) continue;
      out[k] = part.slice(idx + 1).trim();
    }
    return out;
  }

  static #html(str: string): DocumentFragment {
    const tpl = document.createElement('template');
    tpl.innerHTML = str;
    return tpl.content;
  }
}

if (!customElements.get('as-auto-controls')) {
  customElements.define('as-auto-controls', AsAutoControls);
} else {
  console.warn('as-auto-controls already defined');
}
